import React, { useState, useEffect, useMemo, useCallback } from 'react';
import User from '@this/domain/user/user';
import type UserJson from '@this/domain/user/user_json';
import { Fetcher } from '@this/src/util';
import { TabiregiHeader } from './tabiregi_header';

type Props = {
  token: string;
  userJson?: UserJson;
  authError: string | null;
  authStatus: number | null;
};

type InitialData = {
  searchQueryId: number;
  userName: string;
  userEmail: string;
  countryNames: string[];
  registered: boolean;
};

type FetchResponse = {
  success: boolean;
  search_query_id?: number;
  user_name?: string;
  user_email?: string;
  country_names?: string[];
  registered?: boolean;
  error?: string;
};

export const TabiregiRegisterPage: React.FC<Props> = ({ token, userJson, authError, authStatus }) => {
  const styles = useMemo(() => getStyles(), []);
  const user = useMemo(() => (userJson ? new User(userJson) : undefined), [userJson]);
  const [loading, setLoading] = useState<boolean>(!authError);
  const [data, setData] = useState<InitialData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(authError);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (authError) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res: FetchResponse = await Fetcher.get(`/tabiregi.json?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.success && res.search_query_id != null) {
          setData({
            searchQueryId: res.search_query_id,
            userName: res.user_name ?? '',
            userEmail: res.user_email ?? '',
            countryNames: res.country_names ?? [],
            registered: !!res.registered
          });
          setRegistered(!!res.registered);
        } else {
          setErrorMessage(res.error ?? 'リンクの検証に失敗しました。');
        }
      } catch {
        if (!cancelled) setErrorMessage('リンクの検証に失敗しました。');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authError, token]);

  const handleRegister = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await Fetcher.post('/tabiregi_consents/register_by_token.json', { token });
      setRegistered(true);
    } catch {
      setSubmitError('たびレジ登録に失敗しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  return (
    <div style={styles.page}>
      <TabiregiHeader user={user} />
      <main style={styles.main}>
        {loading && <p style={styles.message}>読み込み中...</p>}

        {!loading && errorMessage && (
          <div style={styles.errorBox} data-testid="tabiregi-register-page-error">
            <p>{errorMessage}</p>
            <p style={styles.subText}>お手数ですが、メールに記載のリンクをご確認の上、再度お試しください。</p>
          </div>
        )}

        {!loading && !errorMessage && data && (
          <div style={styles.card}>
            <h1 style={styles.title}>たびレジ登録のご案内</h1>
            <p style={styles.description}>
              下記の出張日程・滞在先・連絡先を外務省の海外旅行登録「たびレジ」へ登録します。
              内容をご確認の上、登録ボタンを押してください。
            </p>
            <dl style={styles.dl}>
              <dt style={styles.dt}>出張者名</dt>
              <dd style={styles.dd}>{data.userName}</dd>
              <dt style={styles.dt}>メールアドレス</dt>
              <dd style={styles.dd}>{data.userEmail}</dd>
              {data.countryNames.length > 0 && (
                <>
                  <dt style={styles.dt}>渡航予定国</dt>
                  <dd style={styles.dd}>{data.countryNames.join('、')}</dd>
                </>
              )}
            </dl>

            {registered ? (
              <div style={styles.successBox} data-testid="tabiregi-register-page-success">
                <p style={styles.successText}>登録が完了しました。</p>
              </div>
            ) : (
              <>
                {submitError && (
                  <p style={styles.submitError} data-testid="tabiregi-register-page-submit-error">
                    {submitError}
                  </p>
                )}
                <div style={styles.buttonArea}>
                  <button
                    type="button"
                    style={
                      submitting
                        ? { ...styles.primaryButton, opacity: 0.5, cursor: 'not-allowed' }
                        : styles.primaryButton
                    }
                    onClick={handleRegister}
                    disabled={submitting}
                    data-testid="tabiregi-register-page-submit"
                  >
                    {submitting ? '登録中...' : 'たびレジに登録する'}
                  </button>
                </div>
              </>
            )}

            <p style={styles.note}>
              ※ 登録後の解除は
              <a href="https://www.ezairyu.mofa.go.jp/tabireg/simple/delete" target="_blank" rel="noreferrer">
                たびレジ登録解除
              </a>
              よりお手続きください。
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

function getStyles(): Record<string, React.CSSProperties> {
  return {
    page: {
      minHeight: '100vh',
      background: '#f5f6f8',
      fontFamily: '"Hiragino Sans", "Hiragino Kaku Gothic ProN", Meiryo, sans-serif',
      color: '#333'
    },
    main: {
      maxWidth: 720,
      margin: '0 auto',
      padding: '32px 16px'
    },
    message: {
      textAlign: 'center',
      color: '#666'
    },
    errorBox: {
      background: '#fff',
      padding: 24,
      borderRadius: 8,
      border: '1px solid #f0c2c2',
      color: '#a94442'
    },
    subText: {
      marginTop: 12,
      color: '#666',
      fontSize: 13
    },
    card: {
      background: '#fff',
      padding: 24,
      borderRadius: 8,
      border: '1px solid #e0e0e0'
    },
    title: {
      fontSize: 20,
      margin: '0 0 16px',
      fontWeight: 'bold'
    },
    description: {
      lineHeight: 1.7,
      marginBottom: 16
    },
    dl: {
      margin: '0 0 24px',
      padding: 0
    },
    dt: {
      fontSize: 12,
      color: '#888',
      marginTop: 12
    },
    dd: {
      margin: '4px 0 0',
      padding: 0,
      fontSize: 15
    },
    successBox: {
      background: '#eef9f0',
      border: '1px solid #c2e1c8',
      borderRadius: 6,
      padding: 16,
      textAlign: 'center'
    },
    successText: {
      color: '#2f7a3a',
      fontWeight: 'bold',
      margin: 0
    },
    submitError: {
      color: '#a94442',
      marginTop: 12
    },
    buttonArea: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: 16
    },
    primaryButton: {
      padding: '12px 32px',
      background: '#1976d2',
      color: '#fff',
      border: 'none',
      borderRadius: 4,
      fontSize: 15,
      fontWeight: 'bold',
      cursor: 'pointer'
    },
    note: {
      marginTop: 24,
      fontSize: 12,
      color: '#666'
    }
  };
}

export default TabiregiRegisterPage;
